/**
 * OperatorLotteryTab — wraps three sub-tabs:
 *   • Place Ticket   — existing AdminLotteryHelperPanel
 *   • Drawings       — last 7 days of winning numbers (states this op has enabled)
 *   • Tickets        — every ticket placed at this storefront w/ filter + scan viewer
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLotteryHelperPanel } from '../pages/admin/AdminLotteryHelperPanel';
import OperatorRecentDraws from './OperatorRecentDraws';
import OperatorTicketsList from './OperatorTicketsList';
import LotteryLimitsPanel from './LotteryLimitsPanel';
import LotteryPayoutEditor from './LotteryPayoutEditor';
import { TicketIcon, Trophy, ListChecks, ShieldCheck, Percent } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SUB_TABS = [
    { key: 'place',    label: 'Place Ticket', icon: TicketIcon },
    { key: 'drawings', label: 'Drawings',     icon: Trophy },
    { key: 'tickets',  label: 'Tickets',      icon: ListChecks },
    { key: 'limits',   label: 'Limits',       icon: ShieldCheck },
    { key: 'payouts',  label: 'Payouts',      icon: Percent },
];

const OperatorLotteryTab = () => {
    const [active, setActive] = useState('place');
    const [cashiers, setCashiers] = useState([]);

    // Pre-load the cashier roster so the "By cashier" filter in Tickets is
    // populated the moment the operator switches sub-tabs.
    useEffect(() => {
        axios.get(`${API}/operator/cashiers`, { withCredentials: true })
            .then(({ data }) => setCashiers(
                (data || []).map(c => ({ id: c.id, name: c.name || c.phone || 'Cashier' }))
            ))
            .catch(() => setCashiers([]));
    }, []);

    return (
        <div className="space-y-5" data-testid="operator-lottery-tab">
            <div className="flex items-center gap-2 border-b border-zinc-800 overflow-x-auto">
                {SUB_TABS.map(t => {
                    const Icon = t.icon;
                    const on = active === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setActive(t.key)}
                            data-testid={`lottery-subtab-${t.key}`}
                            className={`relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition
                                ${on ? 'text-emerald-300' : 'text-zinc-500 hover:text-zinc-300'}`}>
                            <span className="inline-flex items-center gap-1.5">
                                <Icon className="w-3.5 h-3.5" /> {t.label}
                            </span>
                            {on && <span className="absolute left-2 right-2 bottom-0 h-[2px] bg-emerald-400 rounded-full" />}
                        </button>
                    );
                })}
            </div>

            {active === 'place'    && <AdminLotteryHelperPanel playersEndpoint="/cashier/players" />}
            {active === 'drawings' && <OperatorRecentDraws />}
            {active === 'tickets'  && <OperatorTicketsList cashierFilter={cashiers} />}
            {active === 'limits'   && <LotteryLimitsPanel />}
            {active === 'payouts'  && <LotteryPayoutEditor />}
        </div>
    );
};

export default OperatorLotteryTab;
